import { useCallback, useReducer } from 'react';

// Fetch actions:
type StartRequest = { type: 'request' };
type ReceivedError<T> = { type: 'error'; error: T };
type ReceivedResponse<T> = { type: 'response'; response: T };
type Reset = { type: 'reset' };

type FetchAction<T, TError> = StartRequest | ReceivedError<TError> | ReceivedResponse<T> | Reset;

// Fetch action constructors:
const StartRequest: StartRequest = { type: 'request' };
const ReceivedError = <T>(error: T): ReceivedError<T> => ({
  type: 'error',
  error,
});
const ReceivedResponse = <T>(response: T): ReceivedResponse<T> => ({
  type: 'response',
  response,
});
const Reset: Reset = { type: 'reset' };

type InitialState = {
  type: 'initial';
};

type PendingState<TResp, TErr> = { type: 'loading'; response?: TResp; requestError?: TErr };
type SuccessState<TResp> = { type: 'success'; response: TResp };
type ErrorState<TErr> = { type: 'error'; requestError: TErr };
export type FetchState<T = any, TError = string> =
  | PendingState<T, TError>
  | InitialState
  | SuccessState<T>
  | ErrorState<TError>;

function fetchReducer<T, TError>(
  oldState: FetchState<T, TError>,
  action: FetchAction<T, TError>,
): FetchState<T, TError> {
  switch (action.type) {
    case 'request':
      return { ...oldState, type: 'loading' };
    case 'error':
      return { type: 'error', requestError: action.error };
    case 'response':
      return { type: 'success', response: action.response };
    case 'reset':
      return { type: 'initial' };
    default:
      return oldState;
  }
}

export const useFetchReducer = <T = any, TError = string>() => {
  const [state, dispatch] = useReducer(fetchReducer<T, TError>, { type: 'initial' });

  const startRequest = useCallback(() => dispatch(StartRequest), [dispatch]);
  const receivedError = useCallback((error: TError) => dispatch(ReceivedError<TError>(error)), [dispatch]);
  const receivedResponse = useCallback((response: T) => dispatch(ReceivedResponse(response)), [dispatch]);
  const reset = useCallback(() => dispatch(Reset), [dispatch]);

  return {
    state,
    response: state.type === 'loading' || state.type === 'success' ? state.response : undefined,
    requestError: state.type === 'loading' || state.type === 'error' ? state.requestError : undefined,
    loading: state.type === 'loading',
    startRequest,
    receivedError,
    receivedResponse,
    reset,
  };
};
